import React, { useState, useEffect } from "react";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import { ShelterOccupancyPercentageMetric } from "./charts-metrics/ShelterOccupancyPercentageMetric";
import { ShelterIntakeDayMetric } from "./charts-metrics/ShelterIntakeDayMetric";
import { ShelterExitDayMetric } from "./charts-metrics/ShelterExitDayMetric";
import { ShelterVacancyMetric } from "./charts-metrics/ShelterVacancyMetric";
import { ShelterOccupancyLineChart } from "./charts-metrics/ShelterOccupancyLineChart";
import { ShelterOccupiedMetric } from "./charts-metrics/ShelterOccupiedMetric";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { fetchReportShelterIntakesExits, fetchReportShelterOccupancy } from "../../services/httpClient";
import DataTable from "./ui/DataTable";
import ReportTimeInterval from "./ui/ReportTimeInterval";
import { ShelterExitTypePieChart } from "./charts-metrics/ShelterExitTypePieChart";
import { activeKey, reportRunning } from "./Reports";
import { formatDateTimeToLocal } from "../../helpers/date";

interface ShelterTabProps {
  buildPDF: (section: HTMLElement, title: string) => void;
  iPad: boolean;
  isActive: boolean;
}

const ShelterTab = ({ buildPDF, iPad, isActive }: ShelterTabProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [dateInterval, setDateInterval] = useState<number>(365);
  const [data, setData] = useState<any>();
  const [selectedData, setSelectedData] = useState<any[] | null>(null);
  const [activeCard, setActiveCard] = useState<string>("Intakes");

  const intakeExitHeaders = [
    { label: "Name", key: "name" },
    { label: "Intake At", key: "intake_at" },
    { label: "Exit At", key: "exit_at" },
    { label: "Exit Type", key: "exit_type" },
  ];

  useEffect(() => {
    if (activeKey.value === "shelter") {
      const start = new Date();
      start.setDate(start.getDate() - dateInterval);
      const startString = start.toISOString().slice(0, 10);
      const endString = new Date().toISOString().slice(0, 10);
      setStartDate(startString);
      setEndDate(endString);
      getData(startString, endString);
    }
  }, [activeKey.value]);

  const getData = async (start: string, end: string) => {
    const [occupancy, intakeExit] = await Promise.all([getOccupancyData(), getIntakeExitData()]);

    setData({
      occupancy,
      intakes: intakeExit?.intakes,
      exits: intakeExit?.exits,
      start: start,
      end: end,
    });
    setIsLoading(false);
    setActiveCard("Intakes");
  };

  const getOccupancyData = () => {
    return fetchReportShelterOccupancy()
      .then((res: any) => {
        return res[0].data;
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      });
  };

  const getIntakeExitData = () => {
    return fetchReportShelterIntakesExits()
      .then((res: any) => {
        const intakes = res[0].data;
        intakes?.forEach((i: any) => {
          if (i?.intake_at) {
            const localDateTime = formatDateTimeToLocal(i?.intake_at);
            i.intake_at = localDateTime.date;
          }

          if (i?.exit_at) {
            const localDateTime = formatDateTimeToLocal(i?.exit_at);
            i.exit_at = localDateTime.date;
          }
        });
        const exitFiltered = intakes.filter((item: any) => item.exit_at);
        let exits = exitFiltered.map((item: any) => ({
          exit_at: item.exit_at,
          exit_note_id: item.exit_note_id,
          exit_type: item.exit_type,
        }));
        return { intakes, exits };
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      });
  };

  const onClickDownloadReport = () => {
    reportRunning.value = true;
    downloadReport();
  };

  const downloadReport = async () => {
    const reportSection = document.getElementById("shelter-section");
    const reportTitle = document.getElementById("shelter-title");
    const reportTable = document.getElementById("shelter-table");

    if (!reportSection) {
      console.error("Report section not found");
      reportRunning.value = false;
      return;
    }

    if (reportTitle) {
      reportTitle.style.visibility = "hidden";
      reportTitle.style.display = "none";
    }

    if (reportTable) {
      reportTable.style.visibility = "hidden";
      reportTable.style.display = "none";
    }

    const images = reportSection.getElementsByTagName("img");
    for (let i = 0; i < images.length; i++) {
      images[i].crossOrigin = "anonymous";
    }

    await buildPDF(reportSection, "Shelter");
    reportRunning.value = false;

    if (reportTitle) {
      reportTitle.style.visibility = "visible";
      reportTitle.style.display = "flex";
    }

    if (reportTable) {
      reportTable.style.visibility = "visible";
      reportTable.style.display = "flex";
    }
  };

  const onClickCard = (cardId: string) => {
    setActiveCard((prev) => (prev === cardId ? "" : cardId));
  };

  return (
    <div id="shelter-section">
      {isLoading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" style={{ color: "#F6893D" }} />
        </div>
      ) : (
        <>
          <Row id="shelter-title" className="mb-3">
            <Col className="col-8">
              <h4 className="hero-heading app-header-text">Shelter</h4>
            </Col>
            <Col className="text-end col-4">
              {!iPad && (
                <Button className="app-primary-color button" onClick={() => onClickDownloadReport()}>
                  <FontAwesomeIcon icon={faDownload} />
                  {" Report"}
                </Button>
              )}
            </Col>
          </Row>
          <div className="pdf-page">
            {data?.occupancy && (
              <Row className="mb-3 justify-content-center">
                <Col xs={3} className="mb-3">
                  <ShelterVacancyMetric data={data} />
                </Col>
                <Col xs={3} className="mb-3">
                  <ShelterOccupiedMetric data={data} />
                </Col>
                <Col xs={3} className="mb-3">
                  <ShelterOccupancyPercentageMetric data={data} />
                </Col>
              </Row>
            )}
            <ReportTimeInterval
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              dateInterval={dateInterval}
              setDateInterval={setDateInterval}
              intervalOne={30}
              intervalOneText={"Last Month"}
              intervalTwo={90}
              intervalTwoText={"Last 3 Months"}
              intervalThree={180}
              intervalThreeText={"Last 6 Months"}
              intervalFour={365}
              intervalFourText={"Last Year"}
              onClickRefresh={getData}
              hideBtnId="shelter-refresh-button"
            />
            {data?.intakes && (
              <Row id="occupancy-line" className="mb-5 justify-content-center">
                <Col xs={10}>
                  <ShelterOccupancyLineChart data={data} />
                </Col>
              </Row>
            )}
            {data?.intakes && (
              <Row className="mb-5">
                <Col xs={6} className="mb-3">
                  <Row>
                    <Col xs={6} md={5} xl={4} className="mb-3">
                      <ShelterIntakeDayMetric
                        data={data}
                        setSelectedData={setSelectedData}
                        readOnly={false}
                        active={activeCard === "Intakes"}
                        onHandleClick={() => onClickCard("Intakes")}
                      />
                    </Col>
                    <Col xs={6} md={5} xl={4} className="mb-3">
                      <ShelterExitDayMetric
                        data={data}
                        setSelectedData={setSelectedData}
                        readOnly={false}
                        active={activeCard === "Exits"}
                        onHandleClick={() => onClickCard("Exits")}
                      />
                    </Col>
                  </Row>
                  <Col id="shelter-table" xs={12}>
                    {activeCard && selectedData && (
                      <DataTable
                        tableHeadings={intakeExitHeaders}
                        tableData={selectedData}
                        title={activeCard}
                        hideTitle={true}
                        overrideItemsPerPage={10}
                      />
                    )}
                  </Col>
                </Col>
                <Col xs={6}>
                  <ShelterExitTypePieChart data={data} />
                </Col>
              </Row>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ShelterTab;
